import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import CustomModal from "../../../../components/ui/modal/modal.component";
import axios, { useApiRequest } from "../../../../api/axios";
import { CONTRACTS_ENDPOINT, ERROR_SNACKBAR, SUCCESS_SNACKBAR } from "../../../../consts";

/** The contract (and the settlement it was opened from) whose inflation is edited. */
export type ContractInflationTarget = {
  contractId: number;
  contractLabel: string;
  inflationCondition: string | null;
  inflationRatePercent: number | string | null;
  // A confirmed settlement keeps the rate it was confirmed with - the change
  // only reaches the contract's next settlements.
  settlementConfirmed: boolean;
};

// The backend stores "no inflation" as a null condition; the Select can't take
// null, so the empty string stands in for it in the UI only.
const NO_INFLATION_VALUE = "";

// Values of ContractInflationCondition (backend consts.py).
const INFLATION_CONDITION_OPTIONS = [
  { value: NO_INFLATION_VALUE, label: "No inflation" },
  { value: "AL", label: "Always add" },
  { value: "AEI", label: "Add after exceeding the limit" },
];

// Mirrors the contract's DecimalField(max_digits=5, decimal_places=2).
const RATE_PATTERN = /^\d{1,3}([.,]\d{1,2})?$/;

type ContractInflationModalProps = {
  target: ContractInflationTarget | null;
  onClose: () => void;
};

export const ContractInflationModal = ({ target, onClose }: ContractInflationModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, executeRequest } = useApiRequest();
  const [condition, setCondition] = useState(NO_INFLATION_VALUE);
  const [rate, setRate] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) {
      return;
    }
    setCondition(target.inflationCondition ?? NO_INFLATION_VALUE);
    setRate(
      target.inflationRatePercent != null && target.inflationRatePercent !== ""
        ? String(target.inflationRatePercent)
        : ""
    );
    setValidationError(null);
  }, [target]);

  const handleSave = async () => {
    if (!target || loading) {
      return;
    }

    const trimmedRate = rate.trim();
    let ratePayload: string | null = null;
    if (condition && trimmedRate) {
      if (!RATE_PATTERN.test(trimmedRate)) {
        setValidationError(t("Invalid inflation rate percent"));
        return;
      }
      ratePayload = trimmedRate.replace(",", ".");
      if (Number(ratePayload) > 100) {
        setValidationError(t("Inflation rate percent must be between 0 and 100"));
        return;
      }
    }
    setValidationError(null);

    const result = await executeRequest(() =>
      axios.patch(`${CONTRACTS_ENDPOINT}${target.contractId}/inflation/`, {
        inflation_condition: condition || null,
        inflation_rate_percent: ratePayload,
      })
    );

    if (result.success) {
      enqueueSnackbar(t("Inflation rate saved"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("settlementUpdated"));
      window.dispatchEvent(new Event("contractUpdated"));
      onClose();
      return;
    }

    const errorData = result.errorData;
    const fieldError =
      errorData?.inflation_rate_percent?.[0] ?? errorData?.inflation_condition?.[0];
    enqueueSnackbar(
      errorData?.message || errorData?.detail || fieldError || t("Failed to save inflation rate"),
      ERROR_SNACKBAR
    );
  };

  return (
    <CustomModal
      opened={target !== null}
      setOpened={(value) => {
        if (!value) {
          onClose();
        }
      }}
      title={t("Edit inflation rate")}
      width={480}
      minWidth={0}
      minHeight={0}
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {target ? (
            <Typography variant="body2" color="text.secondary">
              {`${t("Contract")}: ${target.contractLabel}`}
            </Typography>
          ) : null}

          {target?.settlementConfirmed ? (
            <Alert severity="warning">
              {t(
                "This settlement is already confirmed and keeps the inflation rate it was confirmed with. The change will apply to the next settlements of this contract."
              )}
            </Alert>
          ) : (
            <Alert severity="info">
              {t(
                "The inflation rate is set on the contract. This settlement and the contract's other unconfirmed settlements will be recalculated with it."
              )}
            </Alert>
          )}

          <TextField
            select
            size="small"
            fullWidth
            label={t("Inflation condition")}
            value={condition}
            onChange={(event) => {
              setCondition(event.target.value);
              setValidationError(null);
            }}
            // "No inflation" is the empty value - keep the label above it
            // instead of over it.
            slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          >
            {INFLATION_CONDITION_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.label)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            fullWidth
            label={t("Inflation rate (%)")}
            value={condition ? rate : ""}
            disabled={!condition}
            onChange={(event) => {
              setRate(event.target.value);
              setValidationError(null);
            }}
            error={!!validationError}
            helperText={
              validationError ??
              (condition ? undefined : t("Choose when inflation is added to set its rate."))
            }
            slotProps={{ htmlInput: { inputMode: "decimal" } }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              {t("Cancel")}
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? t("Processing...") : t("Save inflation rate")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};
